import { Component, EventEmitter, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SaleFormService } from '@app/features/sales/add-sale/steps/sale-form.service';
import { UsersService, UnregisteredUserRequest, UnregisteredUsersService, RetailersService, DistributionChannelsService, Int64ComboValueResponse } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom, Subscription } from 'rxjs';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { SessionService } from '@src/app/shared/services/session.service';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Features } from '@src/app/shared/backend/auth/Feature';

@Component({
    selector: 'app-seller-details',
    templateUrl: './seller-details.component.html',
    styleUrl: './seller-details.component.scss',
    host: {
        class: 'app-seller-details'
    },
    standalone: false
})
export class StepSellerDetailsComponent implements OnInit {

  @Output() stepSaved = new EventEmitter<void>();
  sellerTypeSubscription: Subscription | null = null;
  stepData = {};

  canUseMyselfSeller: boolean = true;

  showErrorSystemNoRetailersAssigned: boolean = false;
  showErrorGuestNoRetailersAssigned: boolean = false;
  
  showDistributionChannel: boolean = false;
  hasUnregisteredUserFeature: boolean = false;

  combosData: { [key: string]: ComboData } = {
    'myselfRetailers': new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    'myselfDistributionChannels': new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),

    'systemUsers': new ComboData(async (filter) => await firstValueFrom(this.usersService.apiUsersFilterComboPost(filter))),
    'systemRetailers': new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    'systemDistributionChannels': new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),

    'guestUsers': new ComboData(async (filter) => await firstValueFrom(this.unregisteredUsersService.apiUnregisteredUsersFilterComboPost(filter))),
    'guestRetailers': new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    'guestDistributionChannels': new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),
    'createGuestRetailers': new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter)))
  }

  private getControlNameForCombo(
    comboType:
      'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers'
  ): string | null {
    switch (comboType) {
      case 'myselfRetailers':
        return 'myselfRetailerId';
      case 'myselfDistributionChannels':
        return 'myselfDistributionChannelId';
      case 'systemUsers':
        return 'systemUserId';
      case 'systemRetailers':
        return 'systemRetailerId';
      case 'systemDistributionChannels':
        return 'systemDistributionChannelId';
      case 'guestUsers':
        return 'guestUserId';
      case 'guestRetailers':
        return 'guestRetailerId';
      case 'guestDistributionChannels':
        return 'guestDistributionChannelId';
      case 'createGuestRetailers':
        return 'createGuestRetailerId';
      default:
        return null;
    }
  }

  private autoSelectSingleOption(
    comboType:
      'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers'
  ): void {
    const controlName = this.getControlNameForCombo(comboType);

    if (!controlName) {
      return;
    }

    const list = this.combosData[comboType].list ?? [];
    const totalRecords = this.combosData[comboType].totalRecords ?? list.length;

    if (totalRecords !== 1 || list.length !== 1) {
      return;
    }

    const onlyOptionId = list[0]?.id;

    if (onlyOptionId == null) {
      return;
    }

    const control = this.sellerDetailsFormGroup.get(controlName);

    if (!control) {
      return;
    }

    const currentValue = Array.isArray(control.value) ? control.value : [];

    if (currentValue.length > 0) {
      return;
    }

    control.setValue([onlyOptionId]);
  }

  private async loadComboData(
    comboType:
      'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers',
    search: string | null = null,
    activePage: number = 0
  ): Promise<void> {
    await this.combosData[comboType].getData(search, activePage);
    this.autoSelectSingleOption(comboType);
  }

  async onLazyLoadComboData(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers',
    event: any
  ) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.loadComboData(comboType, search, activePage);
  }

  getComboTotalPages(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers'
  ): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private clearDropdownControl(controlName: string): void {
    this.sellerDetailsFormGroup.get(controlName)?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get(controlName)?.markAsUntouched();
  }

  private getSingleSelectedId(controlName: string): number | null {
    const value = this.sellerDetailsFormGroup.get(controlName)?.value;
    const id = Array.isArray(value) ? value[0] : null;

    return id != null ? Number(id) : null;
  }

  private getSingleSelectedString(controlName: string): string | null {
    const value = this.sellerDetailsFormGroup.get(controlName)?.value;
    const id = Array.isArray(value) ? value[0] : null;

    return id != null ? String(id) : null;
  }

  private findComboItem(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers',
    id: string | number | null
  ): any | null {
    if (id == null) return null;

    return (this.combosData[comboType].list ?? []).find(
      (item: any) => String(item.id) === String(id)
    ) ?? null;
  }

  private ensureComboItemExists(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels' | 'createGuestRetailers',
    item: any | null | undefined
  ): void {
    if (!item?.id) return;

    const list = this.combosData[comboType].list ?? [];
    const exists = list.some((x: any) => Number(x.id) === Number(item.id));

    if (!exists) {
      this.combosData[comboType].list = [item, ...list];
    }
  }

  public sellerDetailsFormGroup: FormGroup = this.fb.group({
    sellerType: ['myself', [Validators.required]],
    myselfRetailerId: [[], [Validators.required]],
    myselfRetailer: [null],
    myselfDistributionChannelId: [[], [Validators.required]],
    myselfDistributionChannel: [null],
    systemUserId: [[]],
    systemUser: [null],
    systemRetailerId: [[]],
    systemRetailer: [null],
    systemDistributionChannelId: [[]],
    systemDistributionChannel: [null],
    createGuestUser: [false],
    guestUserId: [[]],
    guestUser: [null],
    guestRetailerId: [[]],
    guestRetailer: [null],
    guestDistributionChannelId: [[]],
    guestDistributionChannel: [null],
    firstname: ['', [Validators.maxLength(50)]],
    lastname: ['', [Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    romanisedLastName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    createGuestRetailerId: [[]],
  });

  constructor(
    private fb: FormBuilder,
    private retailersService: RetailersService,
    private distributionChannelsService: DistributionChannelsService,
    private usersService: UsersService,
    private unregisteredUsersService: UnregisteredUsersService,
    private session: SessionService,
    private permissionService: PermissionService,
    private saleFormService: SaleFormService) { }

  async onMyselfRetailerIdChange() {
    const selectedId = this.getSingleSelectedId('myselfRetailerId');

    this.combosData['myselfDistributionChannels'].reset();
    this.clearDropdownControl('myselfDistributionChannelId');

    if (!selectedId) {
      return;
    }

    const response: any = await firstValueFrom(
      this.retailersService.apiRetailersIdDistributionChannelIdsGet(selectedId)
    );

    const distributionChannelsIds = Array.isArray(response) ? response : response?.distributionChannelIds ?? [];

    this.combosData['myselfDistributionChannels'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: distributionChannelsIds.map((id: number) => id.toString())
      }
    ];

    await this.loadComboData('myselfDistributionChannels', null, 0);
  }

  async onSystemUserIdChange() {
    const selectedId = this.getSingleSelectedString('systemUserId');

    this.combosData['systemRetailers'].reset();
    this.clearDropdownControl('systemRetailerId');

    this.combosData['systemDistributionChannels'].reset();
    this.clearDropdownControl('systemDistributionChannelId');

    this.showErrorSystemNoRetailersAssigned = false;

    if (!selectedId) {
      return;
    }

    const response: any = await firstValueFrom(
      this.usersService.apiUsersIdRetailerIdsGet(selectedId)
    );

    const retailersIds = Array.isArray(response) ? response : response?.retailerIds ?? [];

    this.combosData['systemRetailers'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: retailersIds.map((id: number) => id.toString())
      }
    ];

    await this.loadComboData('systemRetailers', null, 0);
    this.showErrorSystemNoRetailersAssigned = retailersIds.length === 0;
  }

  async onSystemRetailerIdChange() {
    const selectedId = this.getSingleSelectedId('systemRetailerId');

    this.combosData['systemDistributionChannels'].reset();
    this.clearDropdownControl('systemDistributionChannelId');

    if (!selectedId) {
      return;
    }

    const response: any = await firstValueFrom(
      this.retailersService.apiRetailersIdDistributionChannelIdsGet(selectedId)
    );

    const distributionChannelsIds = Array.isArray(response) ? response : response?.distributionChannelIds ?? [];

    this.combosData['systemDistributionChannels'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: distributionChannelsIds.map((id: number) => id.toString())
      }
    ];

    await this.loadComboData('systemDistributionChannels', null, 0);
  }

  async onGuestUserIdChange() {
    const selectedId = this.getSingleSelectedId('guestUserId');

    this.combosData['guestRetailers'].reset();
    this.clearDropdownControl('guestRetailerId');

    this.combosData['guestDistributionChannels'].reset();
    this.clearDropdownControl('guestDistributionChannelId');

    this.showErrorGuestNoRetailersAssigned = false;

    if (!selectedId) {
      return;
    }

    const response: any = await firstValueFrom(
      this.unregisteredUsersService.apiUnregisteredUsersIdRetailerIdsGet(selectedId)
    );

    const retailersIds = Array.isArray(response) ? response : response?.retailerIds ?? [];

    this.combosData['guestRetailers'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: retailersIds.map((id: number) => id.toString())
      }
    ];

    await this.loadComboData('guestRetailers', null, 0);
    this.showErrorGuestNoRetailersAssigned = retailersIds.length === 0;
  }

  async onGuestRetailerIdChange() {
    const selectedId = this.getSingleSelectedId('guestRetailerId');

    this.combosData['guestDistributionChannels'].reset();
    this.clearDropdownControl('guestDistributionChannelId');

    if (!selectedId) {
      return;
    }

    const response: any = await firstValueFrom(
      this.retailersService.apiRetailersIdDistributionChannelIdsGet(selectedId)
    );

    const distributionChannelsIds = Array.isArray(response) ? response : response?.distributionChannelIds ?? [];

    this.combosData['guestDistributionChannels'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: distributionChannelsIds.map((id: number) => id.toString())
      }
    ];

    await this.loadComboData('guestDistributionChannels', null, 0);
  }

  onCreateGuestRetailerIdChange(selectedIds: number[] = []) {
    const retailersAssigned = (this.combosData['createGuestRetailers'].list ?? [])
      .filter((item: any) => selectedIds.includes(item.id));

    this.combosData['guestRetailers'].list = retailersAssigned;
    this.combosData['guestRetailers'].totalRecords = retailersAssigned.length;

    const currentGuestRetailerId = this.getSingleSelectedId('guestRetailerId');
    const stillExists = retailersAssigned.some((item: any) => Number(item.id) === Number(currentGuestRetailerId));

    if (!stillExists) {
      this.clearDropdownControl('guestRetailerId');
      this.combosData['guestDistributionChannels'].reset();
      this.clearDropdownControl('guestDistributionChannelId');
    }

    this.autoSelectSingleOption('guestRetailers');
  }

  async onSellerTypeChange(event: any) {
    this.restartForm();
    switch (event.value) {
      case 'myself':
        await this.loadComboData('myselfRetailers', null, 1);
        break;
      case 'system':
        await this.loadComboData('systemUsers', null, 1);
        break;
      case 'guest':
        await this.loadComboData('guestUsers', null, 1);
        break;
    }
    this.toggleMandatoryFieldsValidation(event.value);
  }

  async onCreateGuestUserChange(event: any) {
    this.restartForm();
    if (event.checked === true) {
      await this.loadComboData('createGuestRetailers', null, 1);
    }
    else {
      await this.loadComboData('guestUsers', null, 1);
    }
    this.toggleMandatoryFieldsValidation('guest');
  }

  restartForm() {
    this.sellerDetailsFormGroup.get('myselfRetailerId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('myselfDistributionChannelId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('systemUserId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('systemRetailerId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('systemDistributionChannelId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('guestUserId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('guestRetailerId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('guestDistributionChannelId')?.setValue([], { emitEvent: false });
    this.sellerDetailsFormGroup.get('createGuestRetailerId')?.setValue([], { emitEvent: false });

    this.sellerDetailsFormGroup.get('firstname')?.reset();
    this.sellerDetailsFormGroup.get('lastname')?.reset();
    this.sellerDetailsFormGroup.get('romanisedFirstName')?.reset();
    this.sellerDetailsFormGroup.get('romanisedLastName')?.reset();

    this.showErrorSystemNoRetailersAssigned = false;
    this.showErrorGuestNoRetailersAssigned = false;

    Object.keys(this.combosData).forEach(key => {
      this.combosData[key].reset();
    });
  }

  async ngOnInit() {

    this.updateCanUseMyselfSeller();

    this.hasUnregisteredUserFeature = await this.permissionService.companyHasFeaturePermissions(Features.Sellers);
    this.showDistributionChannel = await this.permissionService.companyHasFeaturePermissions(Features.DistributionChannels);
    this.sellerDetailsFormGroup.get('myselfRetailerId')?.valueChanges.subscribe(() => {
      void this.onMyselfRetailerIdChange();
    });

    this.sellerDetailsFormGroup.get('systemUserId')?.valueChanges.subscribe(() => {
      void this.onSystemUserIdChange();
    });

    this.sellerDetailsFormGroup.get('systemRetailerId')?.valueChanges.subscribe(() => {
      void this.onSystemRetailerIdChange();
    });

    this.sellerDetailsFormGroup.get('guestUserId')?.valueChanges.subscribe(() => {
      void this.onGuestUserIdChange();
    });

    this.sellerDetailsFormGroup.get('guestRetailerId')?.valueChanges.subscribe(() => {
      void this.onGuestRetailerIdChange();
    });

    this.sellerDetailsFormGroup.get('createGuestRetailerId')?.valueChanges.subscribe((selectedIds) => {
      this.onCreateGuestRetailerIdChange(selectedIds || []);
    });

    const sellerData = this.saleFormService.getStepData('sellerData');

    if (sellerData && sellerData.sellerType === 'myself' && !this.canUseMyselfSeller) {
      sellerData.sellerType = 'system';
    }

    if (sellerData) {
      this.sellerDetailsFormGroup.get('sellerType')?.setValue(sellerData.sellerType, { emitEvent: false });
      await this.onSellerTypeChange({ value: sellerData.sellerType });

      if (sellerData.sellerType === 'guest') {
        const createGuestUser = sellerData.createGuestSeller === true;
        this.sellerDetailsFormGroup.get('createGuestUser')?.setValue(createGuestUser, { emitEvent: false });

        if (createGuestUser) {
          await this.onCreateGuestUserChange({ checked: true });

          this.sellerDetailsFormGroup.patchValue({
            firstname: sellerData.firstName ?? '',
            lastname: sellerData.lastName ?? '',
            romanisedFirstName: sellerData.romanisedFirstName ?? '',
            romanisedLastName: sellerData.romanisedLastName ?? '',
          });

          const guestSellerRetailers = sellerData.guestSellerRetailers ?? [];
          const currentCreateGuestRetailers = this.combosData['createGuestRetailers'].list ?? [];
          const missingCreateGuestRetailers = guestSellerRetailers.filter(
            (selected: any) => !currentCreateGuestRetailers.some((x: any) => Number(x.id) === Number(selected.id))
          );

          if (missingCreateGuestRetailers.length) {
            this.combosData['createGuestRetailers'].list = [
              ...missingCreateGuestRetailers,
              ...currentCreateGuestRetailers
            ];
          }

          this.sellerDetailsFormGroup.get('createGuestRetailerId')?.setValue(
            guestSellerRetailers.map((r: any) => r.id),
            { emitEvent: false }
          );
          this.onCreateGuestRetailerIdChange(guestSellerRetailers.map((r: any) => r.id));

          this.ensureComboItemExists('guestRetailers', sellerData.retailer);
          this.sellerDetailsFormGroup.get('guestRetailerId')?.setValue(
            sellerData.retailer?.id ? [sellerData.retailer.id] : [],
            { emitEvent: false }
          );
          await this.onGuestRetailerIdChange();

          this.ensureComboItemExists('guestDistributionChannels', sellerData.distributionChannel);
          this.sellerDetailsFormGroup.get('guestDistributionChannelId')?.setValue(
            sellerData.distributionChannel?.id ? [sellerData.distributionChannel.id] : [],
            { emitEvent: false }
          );
        } else {
          this.ensureComboItemExists('guestUsers', sellerData.unregisteredUser);
          this.sellerDetailsFormGroup.get('guestUserId')?.setValue(
            sellerData.unregisteredUser?.id ? [sellerData.unregisteredUser.id] : [],
            { emitEvent: false }
          );
          await this.onGuestUserIdChange();

          this.ensureComboItemExists('guestRetailers', sellerData.retailer);
          this.sellerDetailsFormGroup.get('guestRetailerId')?.setValue(
            sellerData.retailer?.id ? [sellerData.retailer.id] : [],
            { emitEvent: false }
          );
          await this.onGuestRetailerIdChange();

          this.ensureComboItemExists('guestDistributionChannels', sellerData.distributionChannel);
          this.sellerDetailsFormGroup.get('guestDistributionChannelId')?.setValue(
            sellerData.distributionChannel?.id ? [sellerData.distributionChannel.id] : [],
            { emitEvent: false }
          );
        }
      } else if (sellerData.sellerType === 'myself') {
        this.ensureComboItemExists('myselfRetailers', sellerData.retailer);
        this.sellerDetailsFormGroup.get('myselfRetailerId')?.setValue(
          sellerData.retailer?.id ? [sellerData.retailer.id] : [],
          { emitEvent: false }
        );
        await this.onMyselfRetailerIdChange();

        this.ensureComboItemExists('myselfDistributionChannels', sellerData.distributionChannel);
        this.sellerDetailsFormGroup.get('myselfDistributionChannelId')?.setValue(
          sellerData.distributionChannel?.id ? [sellerData.distributionChannel.id] : [],
          { emitEvent: false }
        );
      } else if (sellerData.sellerType === 'system') {
        this.ensureComboItemExists('systemUsers', sellerData.user);
        this.sellerDetailsFormGroup.get('systemUserId')?.setValue(
          sellerData.user?.id ? [sellerData.user.id] : [],
          { emitEvent: false }
        );
        await this.onSystemUserIdChange();

        this.ensureComboItemExists('systemRetailers', sellerData.retailer);
        this.sellerDetailsFormGroup.get('systemRetailerId')?.setValue(
          sellerData.retailer?.id ? [sellerData.retailer.id] : [],
          { emitEvent: false }
        );
        await this.onSystemRetailerIdChange();

        this.ensureComboItemExists('systemDistributionChannels', sellerData.distributionChannel);
        this.sellerDetailsFormGroup.get('systemDistributionChannelId')?.setValue(
          sellerData.distributionChannel?.id ? [sellerData.distributionChannel.id] : [],
          { emitEvent: false }
        );
      }

      this.toggleMandatoryFieldsValidation(this.sellerDetailsFormGroup.get('sellerType')?.value);
      return;
    }

    if (this.canUseMyselfSeller) {
      await this.loadComboData('myselfRetailers', null, 1);
      this.toggleMandatoryFieldsValidation('myself');
    } else {
      this.sellerDetailsFormGroup.get('sellerType')?.setValue('system', { emitEvent: false });
      await this.loadComboData('systemUsers', null, 1);
      this.toggleMandatoryFieldsValidation('system');
    }

  }

  toggleMandatoryFieldsValidation(value: string) {
    const myselfRetailerIdControl = this.sellerDetailsFormGroup.get('myselfRetailerId');
    const myselfDistributionChannelIdControl = this.sellerDetailsFormGroup.get('myselfDistributionChannelId');

    const systemUserIdControl = this.sellerDetailsFormGroup.get('systemUserId');
    const systemRetailerIdControl = this.sellerDetailsFormGroup.get('systemRetailerId');
    const systemDistributionChannelIdControl = this.sellerDetailsFormGroup.get('systemDistributionChannelId');

    const guestUserIdControl = this.sellerDetailsFormGroup.get('guestUserId');
    const guestRetailerIdControl = this.sellerDetailsFormGroup.get('guestRetailerId');
    const guestDistributionChannelIdControl = this.sellerDetailsFormGroup.get('guestDistributionChannelId');
    const firstnameControl = this.sellerDetailsFormGroup.get('firstname');
    const lastnameControl = this.sellerDetailsFormGroup.get('lastname');
    const createGuestRetailerIdControl = this.sellerDetailsFormGroup.get('createGuestRetailerId');

    // Reset all validators
    myselfRetailerIdControl?.clearValidators();
    myselfDistributionChannelIdControl?.clearValidators();
    systemUserIdControl?.clearValidators();
    systemRetailerIdControl?.clearValidators();
    systemDistributionChannelIdControl?.clearValidators();
    guestUserIdControl?.clearValidators();
    guestRetailerIdControl?.clearValidators();
    guestDistributionChannelIdControl?.clearValidators();
    firstnameControl?.clearValidators();
    lastnameControl?.clearValidators();
    createGuestRetailerIdControl?.clearValidators();

    switch (value) {
      case 'myself':
        myselfRetailerIdControl?.setValidators([Validators.required]);
        if(this.showDistributionChannel)
        {
            myselfDistributionChannelIdControl?.setValidators([Validators.required]);
        }
        break;
      case 'system':
        systemUserIdControl?.setValidators([Validators.required]);
        systemRetailerIdControl?.setValidators([Validators.required]);
        if(this.showDistributionChannel)
        {
           systemDistributionChannelIdControl?.setValidators([Validators.required]);
        }
        break;
      case 'guest':
        if (this.sellerDetailsFormGroup.get('createGuestUser')?.value === true) {
          firstnameControl?.setValidators([Validators.required, Validators.maxLength(50)]);
          lastnameControl?.setValidators([Validators.required, Validators.maxLength(50)]);
          createGuestRetailerIdControl?.setValidators([Validators.required]);
        }
        else {
          guestUserIdControl?.setValidators([Validators.required]);
        }
        guestRetailerIdControl?.setValidators([Validators.required]);
        if(this.showDistributionChannel)
        {
          guestDistributionChannelIdControl?.setValidators([Validators.required]);
        }
        break;
    }

    myselfRetailerIdControl?.updateValueAndValidity();
    myselfDistributionChannelIdControl?.updateValueAndValidity();
    systemUserIdControl?.updateValueAndValidity();
    systemRetailerIdControl?.updateValueAndValidity();
    systemDistributionChannelIdControl?.updateValueAndValidity();
    guestUserIdControl?.updateValueAndValidity();
    guestRetailerIdControl?.updateValueAndValidity();
    guestDistributionChannelIdControl?.updateValueAndValidity();
    firstnameControl?.updateValueAndValidity();
    lastnameControl?.updateValueAndValidity();
    createGuestRetailerIdControl?.updateValueAndValidity();
  }

  async onSaveStepData() {
    const sellerType = this.sellerDetailsFormGroup.get('sellerType')?.value;
    const currentUserId = this.session.user?.id;

    let sellerData: any = {};

    if (sellerType === 'myself') {
      const userId = currentUserId;

      let userMyself = null;
      if (userId) {
        userMyself = await firstValueFrom(this.usersService.apiUsersComboValueIdGet(userId));
      }

      const myselfRetailerId = this.getSingleSelectedId('myselfRetailerId');
      const myselfDistributionChannelId = this.getSingleSelectedId('myselfDistributionChannelId');

      sellerData = {
        sellerType,
        userId,
        user: userMyself,
        unregisteredUserId: null,
        unregisteredUser: null,
        createGuestSeller: false,
        guestSellerRetailerId: null,
        guestSellerRetailer: null,
        guestSellerRetailers: [],
        firstName: null,
        lastName: null,
        romanisedFirstName: null,
        romanisedLastName: null,
        retailerId: myselfRetailerId,
        retailer: this.findComboItem('myselfRetailers', myselfRetailerId),
        distributionChannelId: myselfDistributionChannelId,
        distributionChannel: this.findComboItem('myselfDistributionChannels', myselfDistributionChannelId),
      };

    } else if (sellerType === 'system') {
      const systemUserId = this.getSingleSelectedString('systemUserId');
      const systemRetailerId = this.getSingleSelectedId('systemRetailerId');
      const systemDistributionChannelId = this.getSingleSelectedId('systemDistributionChannelId');

      sellerData = {
        sellerType,
        userId: systemUserId,
        user: this.findComboItem('systemUsers', systemUserId),
        unregisteredUserId: null,
        unregisteredUser: null,
        createGuestSeller: false,
        guestSellerRetailerId: null,
        guestSellerRetailer: null,
        guestSellerRetailers: [],
        firstName: null,
        lastName: null,
        romanisedFirstName: null,
        romanisedLastName: null,
        retailerId: systemRetailerId,
        retailer: this.findComboItem('systemRetailers', systemRetailerId),
        distributionChannelId: systemDistributionChannelId,
        distributionChannel: this.findComboItem('systemDistributionChannels', systemDistributionChannelId),
      };

    } else if (sellerType === 'guest') {
      if (this.sellerDetailsFormGroup.get('createGuestUser')?.value === true) {
        const retailerIds = this.sellerDetailsFormGroup.get('createGuestRetailerId')?.value || [];

        const payload: UnregisteredUserRequest = {
          firstName: this.sellerDetailsFormGroup.controls['firstname'].value,
          lastName: this.sellerDetailsFormGroup.controls['lastname'].value,
          romanisedFirstName: this.sellerDetailsFormGroup.controls['romanisedFirstName'].value,
          romanisedLastName: this.sellerDetailsFormGroup.controls['romanisedLastName'].value,
          retailerIds
        };

        const newGuestId = await firstValueFrom(this.unregisteredUsersService.apiUnregisteredUsersCreatePost(payload));
        const newGuest = await firstValueFrom(this.unregisteredUsersService.apiUnregisteredUsersComboValueIdGet(newGuestId));

        const guestRetailerId = this.getSingleSelectedId('guestRetailerId');
        const guestDistributionChannelId = this.getSingleSelectedId('guestDistributionChannelId');

        const guestSellerRetailers = (this.combosData['createGuestRetailers'].list ?? [])
          .filter((item: any) => retailerIds.includes(item.id));

        sellerData = {
          sellerType,
          userId: null,
          user: null,
          unregisteredUserId: newGuestId,
          unregisteredUser: newGuest,
          createGuestSeller: true,
          guestSellerRetailerId: null,
          guestSellerRetailer: null,
          guestSellerRetailers,
          firstName: this.sellerDetailsFormGroup.controls['firstname'].value,
          lastName: this.sellerDetailsFormGroup.controls['lastname'].value,
          romanisedFirstName: this.sellerDetailsFormGroup.controls['romanisedFirstName'].value,
          romanisedLastName: this.sellerDetailsFormGroup.controls['romanisedLastName'].value,
          retailerId: guestRetailerId,
          retailer: this.findComboItem('guestRetailers', guestRetailerId),
          distributionChannelId: guestDistributionChannelId,
          distributionChannel: this.findComboItem('guestDistributionChannels', guestDistributionChannelId),
        };
      } else {
        const guestUserId = this.getSingleSelectedId('guestUserId');
        const guestRetailerId = this.getSingleSelectedId('guestRetailerId');
        const guestDistributionChannelId = this.getSingleSelectedId('guestDistributionChannelId');

        sellerData = {
          sellerType,
          userId: null,
          user: null,
          unregisteredUserId: guestUserId,
          unregisteredUser: this.findComboItem('guestUsers', guestUserId),
          createGuestSeller: false,
          guestSellerRetailerId: null,
          guestSellerRetailer: null,
          guestSellerRetailers: [],
          firstName: null,
          lastName: null,
          romanisedFirstName: null,
          romanisedLastName: null,
          retailerId: guestRetailerId,
          retailer: this.findComboItem('guestRetailers', guestRetailerId),
          distributionChannelId: guestDistributionChannelId,
          distributionChannel: this.findComboItem('guestDistributionChannels', guestDistributionChannelId),
        };
      }
    }

    this.saleFormService.setStepData('sellerData', sellerData);
    this.stepSaved.emit();
  }

  updateCanUseMyselfSeller(): void {
    const userDetails = this.session.getUserDetails();

    const companyId = userDetails?.company;
    const currentCompanyId = userDetails?.currentCompany || this.session.getCompany();

    this.canUseMyselfSeller =
      !companyId ||
      !currentCompanyId ||
      String(companyId) === String(currentCompanyId);

    if (!this.canUseMyselfSeller && this.sellerDetailsFormGroup.get('sellerType')?.value === 'myself') {
      this.sellerDetailsFormGroup.get('sellerType')?.setValue('system', { emitEvent: false });
    }
  }
}
